import { Check, Car, Sofa, Crown } from "lucide-react";

export const SERVICE_PLANS = [
    {
        id: "premium-exterior",
        name: "Premium Exterior",
        price: "$75–$95",
        duration: "~2 hrs",
        icon: Car,
        tagline: "Deep wash, wax, and finish that pops.",
        priceNote: "Prices vary based on vehicle class",
        features: [
            "Hand wash with pH-neutral soap",
            "Wheel, tire & barrel deep clean",
            "Bug & tar spot removal",
            "Tire dressing",
            "Streak-free glass polish",
        ],
    },
    {
        id: "premium-interior",
        name: "Premium Interior",
        price: "$220",
        duration: "~2.5 hrs",
        icon: Sofa,
        tagline: "Cabin-fresh, like it just rolled off the lot.",
        priceNote: "Add-ons not included in starting price",
        features: [
            "Full vacuum (seats, carpet, trunk)",
            "Hot-water carpet & mat extraction",
            "Seat deep clean (cloth or leather)",
            "Leather conditioning (if applicable)",
            "Dash, console & panel cleanse",
            "Air vent & crevice detailing",
        ],
    },
    {
        id: "full-detail",
        name: "Full Detail",
        price: "$280",
        duration: "~4–5 hrs",
        icon: Crown,
        badge: "Most Popular",
        secondaryBadge: "Best Value",
        tagline: "The complete reset — inside and out.",
        features: [
            "Everything in Exterior + Interior",
            "Hand wash with pH-neutral soap",
            "Wheel, tire & barrel deep clean",
            "Bug & tar spot removal",
            "Tire dressing",
            "Streak-free glass polish",
            "Full vacuum (seats, carpet, trunk)",
            "Hot-water carpet & mat extraction",
            "Seat deep clean (cloth or leather)",
            "Leather conditioning (if applicable)",
            "Dash, console & panel cleanse",
            "Air vent & crevice detailing",
        ],
    },
];

export default function Services({ onSelectPackage }) {
    return (
        <section
            id="services"
            data-testid="services-section"
            className="relative py-24 md:py-32 px-6 md:px-12 bg-[#0A0A0A]"
        >
            <div className="max-w-7xl mx-auto">
                <div className="max-w-2xl mb-16">
                    <div className="font-heading text-xs uppercase tracking-[0.32em] text-[#9D4CDD] font-bold mb-4">
                        / Service Packages
                    </div>
                    <h2
                        data-testid="services-heading"
                        className="font-heading text-4xl md:text-5xl font-black uppercase tracking-tighter text-white"
                    >
                        Three plans.{" "}
                        <span className="text-[#9D4CDD]">One obsession.</span>
                    </h2>
                    <p className="mt-5 text-white/60 leading-relaxed">
                        Pick a base plan, stack optional add-ons, and we&apos;ll
                        show up at your door in Surprise with everything we need.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
                    {SERVICE_PLANS.map((plan) => {
                        const Icon = plan.icon;
                        const highlight = plan.badge === "Most Popular";
                        return (
                            <div
                                key={plan.id}
                                data-testid={`service-card-${plan.id}`}
                                className={`group relative bg-[#121212] border p-8 transition-all duration-300 flex flex-col ${
                                    highlight
                                        ? "border-[#9D4CDD]/50 shadow-[0_0_40px_-15px_rgba(157,76,221,0.55)]"
                                        : "border-white/5 hover:border-[#9D4CDD]/40"
                                }`}
                            >
                                {highlight && (
                                    <span className="absolute top-0 right-0 bg-[#9D4CDD] text-white text-[10px] font-heading font-bold uppercase tracking-[0.24em] px-3 py-1">
                                        {plan.badge}
                                    </span>
                                )}
                                {plan.secondaryBadge && (
                                    <span className="absolute top-7 right-0 bg-[#F5C518] text-black text-[10px] font-heading font-bold uppercase tracking-[0.24em] px-3 py-1">
                                        {plan.secondaryBadge}
                                    </span>
                                )}

                                <div className="flex items-center justify-between mb-8">
                                    <div className="w-12 h-12 flex items-center justify-center border border-[#9D4CDD]/30 bg-[#9D4CDD]/10">
                                        <Icon
                                            className="text-[#9D4CDD]"
                                            size={22}
                                        />
                                    </div>
                                    <span className="font-heading text-[10px] uppercase tracking-[0.24em] text-white/40">
                                        {plan.duration}
                                    </span>
                                </div>

                                <h3 className="font-heading text-2xl font-bold uppercase tracking-tight text-white">
                                    {plan.name}
                                </h3>
                                <p className="mt-2 text-sm text-white/55">
                                    {plan.tagline}
                                </p>

                                <div className="mt-6 flex items-baseline gap-2">
                                    <span className="font-heading text-5xl font-black text-[#9D4CDD] tracking-tighter">
                                        {plan.price}
                                    </span>
                                    <span className="text-xs text-white/40 uppercase tracking-[0.18em]">
                                        starting
                                    </span>
                                </div>
                                {plan.priceNote && (
                                    <p className="mt-2 text-xs text-white/50 leading-relaxed">
                                        {plan.priceNote}
                                    </p>
                                )}

                                <div className="my-7 h-px bg-white/5" />

                                <ul className="space-y-3 flex-1">
                                    {plan.features.map((f) => (
                                        <li
                                            key={f}
                                            className="flex items-start gap-3 text-sm text-white/75"
                                        >
                                            <Check
                                                size={14}
                                                className="text-[#9D4CDD] mt-1 shrink-0"
                                            />
                                            <span>{f}</span>
                                        </li>
                                    ))}
                                </ul>

                                <button
                                    data-testid={`service-select-${plan.id}-btn`}
                                    onClick={() => onSelectPackage?.(plan.name)}
                                    className="mt-8 inline-flex items-center justify-center gap-2 w-full bg-transparent text-white border border-white/20 py-4 font-heading text-[11px] font-bold tracking-[0.28em] uppercase hover:bg-[#9D4CDD] hover:border-[#9D4CDD] transition-all"
                                >
                                    Select Plan
                                </button>
                            </div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}
